import environment from '../../.configs/app/environment'
import { captureError } from './use-sentry.js'

// `posthog-js` is imported dynamically (below, inside `initPostHog`) so its
// SDK code is code-split out of the boot-critical entry chunk, mirroring the
// pattern already used for `@sentry/solid` in `use-sentry.js`. Analytics
// isn't needed for first paint, so keeping it out of the eagerly-parsed boot
// bundle speeds up initial render on marketing pages.
let posthog = null
let isInitialized = false
let loadPromise = null

const isBrowser = () => typeof window !== 'undefined'
const isConfigured = () => Boolean(environment.posthog?.key)

/**
 * Mirrors the API utility `isCompanyEmail` in
 * tenant-lodgr-api/src/utilities/company-email-check.js. Keep in sync with
 * that file so the `is_company_email` person property tracks the same
 * population the backend already flags as internal.
 *
 * @param {string} email
 * @returns {boolean}
 */
export const isCompanyEmail = (email) => {
    if (!email || typeof email !== 'string') return false
    const lower = email.toLowerCase()
    return lower.endsWith('@lodgr.co') || lower.endsWith('@scrawlr.com')
}

/**
 * Build the person-properties payload from a user record loaded via
 * `UserService.show()`.
 *
 * Three-state semantics for `is_company_email`:
 *   - `true`  — identified user whose email matches `isCompanyEmail`.
 *   - `false` — identified user whose email does NOT match (set explicitly,
 *               not omitted, so PostHog filters can distinguish "external
 *               user" from "never-identified anonymous traffic").
 *   - unset   — never-identified anonymous user (no Person record yet, or a
 *               Person from before this property was tracked).
 *
 * The property is named after the underlying check (email-domain match), not
 * the inferred status. A future `is_employee` property can be added later if
 * Lodgr ever wants to track employment as a separate concern (e.g. flagging
 * contractors using personal emails).
 *
 * Configure PostHog's "Internal users" filter on `is_company_email = true`.
 *
 * Extend this function as additional MVP-available user properties are
 * introduced.
 */
export const personPropertiesFromUser = (user) => {
    if (!user) return {}
    return {
        is_company_email: isCompanyEmail(user.email),
    }
}

/**
 * Decode a JWT payload (the middle segment) without verifying the signature.
 * Used by the identity effect in main.jsx to read `user_id` from the Lodgr
 * session token. Safe because:
 *   - We only ever decode our own token (already in our session storage).
 *   - We do not use the decoded values for any security decision; the API is
 *     the authoritative gate for every request.
 *
 * --- WHY THIS HELPER EXISTS (and how to remove it later) -----------------
 * The Lodgr API's `GET /user` response on qa2/prod currently returns only
 * `{ email, verified_at, account_role }` — it does NOT include `user_id`.
 * That matches the documented `UserResponse` schema in
 * `tenant-lodgr-api/spec.yaml` (the schema omits `user_id`, even though the
 * controller/service implementation fetches the full row).
 *
 * Until the API spec drift is fixed, decoding `user_id` from the JWT (which
 * DOES carry it — see `tenant-lodgr-api/src/app/services/session-service.js`
 * and `core-auth-service.js`, both put `user_id` and `profile_id` into the
 * signed JWT payload) is the only reliable way to obtain the canonical Lodgr
 * account identifier on the client.
 *
 * Future cleanup (separate ticket — see plan §"Pinned items requiring product
 * alignment", item "API spec drift: UserResponse in spec.yaml omits user_id"):
 *   1. Add `user_id` (and ideally `profile_id`) to the `UserResponse` schema
 *      in `tenant-lodgr-api/spec.yaml`.
 *   2. Confirm `userService.show()` actually serializes those fields (they
 *      are already in the spread of the DB row, but `additionalProperties:
 *      false` in Fastify's response schema may strip them).
 *   3. Replace this function and its caller in `main.jsx`'s identify effect
 *      with a direct read of `userShowData.user_id`. Remove this helper and
 *      the `await sessionStore.getAccessToken()` step from the effect.
 *
 * @param {string} token - JWT in `header.payload.signature` form.
 * @returns {object|null} The decoded payload object, or null on any failure.
 */
export const decodeJwtPayload = (token) => {
    if (!token || typeof token !== 'string') return null
    try {
        const payloadSegment = token.split('.')[1]
        if (!payloadSegment) return null
        // Convert base64url to base64 and pad to a multiple of 4.
        const base64 = payloadSegment.replaceAll('-', '+').replaceAll('_', '/')
        const padded = base64 + '='.repeat((4 - base64.length % 4) % 4)
        return JSON.parse(atob(padded))
    } catch {
        return null
    }
}

/**
 * Initialize PostHog. Idempotent; safe to call multiple times. No-ops in SSR
 * contexts and when `VITE_APP_POSTHOG_KEY` is blank (acts as a per-env deploy-
 * time kill switch).
 *
 * Sets the `env` super property so every captured event carries the current
 * environment (workstation, qa1, qa2, qa3, development, staging, production).
 * All envs share a single PostHog project; dashboards filter on `env` to
 * separate environments instead of using multiple projects.
 *
 * The SDK itself is dynamically imported here (not at module scope), so
 * calling this is what actually pulls `posthog-js` into a separate chunk and
 * downloads/parses/executes it — callers that want to keep it off the boot
 * path should defer *calling* `initPostHog()` (e.g. via `requestIdleCallback`),
 * not just rely on this being lazy internally.
 *
 * @param {object} [options]
 * @param {boolean} [options.optOutByDefault=false] - When true, PostHog is
 *     initialized with capturing disabled. Used by `main.jsx` when the user
 *     has previously declined consent (`lodgr.consent.analytics === 'rejected'`)
 *     so no events are captured between `posthog.init()` and the
 *     `ConsentProvider.onMount` defensive opt-out. Because the SDK import
 *     itself is async, the opt-out is baked into the very first `posthog.init()`
 *     call — there is no window where the loaded SDK could capture before
 *     opt-out is applied. The user can still opt back in at runtime via
 *     `ConsentContext.accept()` → `optIn()`.
 * @returns {Promise<void>|undefined} Resolves once init has run (or been
 *     skipped). Callers may await this, but no existing caller needs to.
 */
export const initPostHog = ({
    optOutByDefault = false,
} = {}) => {
    if (!isBrowser()) return
    if (isInitialized || loadPromise) return loadPromise
    if (!isConfigured()) return

    loadPromise = (async () => {
        try {
            const posthogModule = await import('posthog-js')
            posthog = posthogModule.default

            posthog.init(environment.posthog.key, {
                api_host: environment.posthog.host || 'https://us.i.posthog.com',
                loaded: (ph) => {
                    ph.register({
                        env: environment.app.env,
                    })
                    if (environment.posthog.debug && !environment.app.isProd) {
                        ph.debug()
                    }
                },
                opt_out_capturing_by_default: optOutByDefault,
            })
            isInitialized = true

            // Expose the SDK on window in non-prod environments so engineers can
            // call `posthog.get_distinct_id()`, `posthog.identify(...)`, etc. from
            // the DevTools console for debugging. The npm-imported singleton does
            // not auto-attach to window the way the HTML snippet method does.
            // Production stays silent — the project key is already public so this
            // is not a security boundary, but there's no reason to expose a debug
            // surface in production.
            if (!environment.app.isProd) {
                window.posthog = posthog
            }
        } catch (error) {
            captureError(error)
        }
    })()

    return loadPromise
}

/**
 * Capture a product-analytics event. No-ops when PostHog has not been
 * initialized (e.g. env-var kill switch). Errors are forwarded to Sentry
 * so the UI is never disrupted by analytics failures.
 */
export const capture = (eventName, properties = {}) => {
    if (!isInitialized) return
    try {
        posthog.capture(eventName, properties)
    } catch (error) {
        captureError(error)
    }
}

/**
 * Identify the current user. `userId` must be the canonical Lodgr account
 * identifier (`user_id` UUID returned by `GET /user`). Any pre-login events
 * captured against the anonymous distinct_id will merge to this user on the
 * first identify call after sign-in.
 */
export const identify = (userId, personProperties = {}) => {
    if (!isInitialized) return
    if (!userId) return
    try {
        posthog.identify(userId, personProperties)
    } catch (error) {
        captureError(error)
    }
}

/**
 * Update Person properties without re-identifying. Use when an already-
 * identified user gains/loses a property (e.g. `is_company_email` flip).
 */
export const setPersonProperties = (properties = {}) => {
    if (!isInitialized) return
    try {
        posthog.setPersonProperties(properties)
    } catch (error) {
        captureError(error)
    }
}

/**
 * Clear the PostHog session and generate a fresh anonymous distinct_id. Must
 * be called on every logout site so the next user on the same browser is not
 * attributed to the previous user.
 */
export const reset = () => {
    if (!isInitialized) return
    try {
        posthog.reset()
    } catch (error) {
        captureError(error)
    }
}

/**
 * Opt the current user back into capture. Called by `ConsentContext.accept()`
 * when the user clicks Accept on the cookie banner (or via the future
 * "Cookie preferences" revocation link). PostHog persists the opt-in state
 * in its own localStorage/cookie so it survives reloads independently of
 * our `lodgr.consent.analytics` key.
 *
 * Together with `optOut`, this is the only programmatic way to toggle
 * capture without changing `opt_out_capturing_by_default` at init time.
 */
export const optIn = () => {
    if (!isInitialized) return
    try {
        posthog.opt_in_capturing()
    } catch (error) {
        captureError(error)
    }
}

/**
 * Opt the current user out of all PostHog capture. Called by
 * `ConsentContext.decline()` when the user clicks Decline on the cookie
 * banner, and defensively on app boot when `lodgr.consent.analytics ===
 * 'rejected'`. After this is called, subsequent `posthog.capture()` /
 * `identify()` calls are silently dropped by the SDK — no extra guarding
 * is required at firing sites.
 */
export const optOut = () => {
    if (!isInitialized) return
    try {
        posthog.opt_out_capturing()
    } catch (error) {
        captureError(error)
    }
}

/**
 * Read PostHog's own opt-out state (independent of our
 * `lodgr.consent.analytics` localStorage key). Returns `false` when the SDK
 * is not initialized so callers do not have to guard the boolean specially.
 */
export const hasOptedOut = () => {
    if (!isInitialized) return false
    try {
        return posthog.has_opted_out_capturing()
    } catch (error) {
        captureError(error)
        return false
    }
}

/**
 * Whether PostHog is live in this session. False when the SDK has not been
 * initialized (SSR, or the `VITE_APP_POSTHOG_KEY` deploy-time kill switch).
 * Callers that gate UI on an experiment use this to decide whether to wait
 * for a flag value or go straight to the default experience.
 *
 * No caller today: the homepage hero experiment that used this concluded in
 * TENANT-5213. Kept, with {@link getFeatureFlag} and {@link onFeatureFlags},
 * as the ready-made surface for the next experiment.
 */
export const isPostHogActive = () => isInitialized

/**
 * Whether this build has a PostHog project key at all. Differs from {@link isPostHogActive}
 * while the SDK is still downloading: configured-but-not-active means "wait for it", not
 * "no experiment" (TENANT-5815: a hero experiment that read the latter sent fast visitors to
 * the default arm with no exposure).
 */
export const isPostHogConfigured = () => isBrowser() && isConfigured()

/**
 * Resolves once `initPostHog` has finished — at once when it never started (no key, not a
 * browser). Never rejects: init failures are captured inside `initPostHog`, so callers can
 * simply fall through to their default.
 * @returns {Promise<void>}
 */
export const whenPostHogReady = () => (loadPromise ?? Promise.resolve()).catch(captureError)

/**
 * Read a feature flag's value for the current distinct_id.
 *
 * IMPORTANT: calling this is what records experiment exposure (PostHog emits
 * `$feature_flag_called`), so it MUST be called at the point the variant is
 * actually used to render — not merely to peek at config. For a multivariate
 * experiment flag this returns the variant key (e.g. `'control' | 'a' | 'b'`),
 * `false` when the flag is disabled for the user, or `undefined` before flags
 * have loaded / when the SDK is not initialized.
 *
 * @param {string} key - The feature-flag key.
 * @returns {string|boolean|undefined}
 */
export const getFeatureFlag = (key) => {
    if (!isInitialized) return
    try {
        return posthog.getFeatureFlag(key)
    } catch (error) {
        captureError(error)
        return
    }
}

/**
 * Subscribe to feature-flag availability. The callback fires once flags have
 * loaded for the current distinct_id and again whenever they change (e.g.
 * after `identify()`). Use this to re-read a flag with {@link getFeatureFlag}
 * once its value is known, so a cold first paint can hold render until the
 * variant resolves.
 *
 * @param {(flags: string[], variants: Record<string, string|boolean>) => void} callback
 * @returns {() => void} Unsubscribe function. A no-op when the SDK is not
 *   initialized, so callers can always `onCleanup(unsubscribe)` safely.
 */
export const onFeatureFlags = (callback) => {
    if (!isInitialized) return () => {}
    try {
        return posthog.onFeatureFlags(callback)
    } catch (error) {
        captureError(error)
        return () => {}
    }
}
